import JamSpinner from '@/src/components/common/JamSpinner/JamSpinner';
import { useApi } from '@/src/store/api.context';
import { useUser } from '@/src/store/user.context';
import { JamProfile } from '@/src/types/JamProfile';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { localLogger } from '@/src/utils/log.utils';
import {
  Box,
  Button,
  Container,
  ContentLayout,
  FormField,
  Grid,
  Header,
  Input,
  InputProps,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import './index.scss';

type LanguageOption = {
  value: string;
  label: string;
};

export interface ILanguageSelectProps {
  /** selected language code **/
  lang: LanguageOption;
  /** Available langauges */
  options: LanguageOption[];
  /** on language changed callback */
  onLanguageSelect: (code: string) => void;
}

const jamProfileDetails: React.FC = () => {
  const { jamProfileAPI } = useApi();
  const { t } = useTranslation();
  const { user, onNicknameChange } = useUser();
  const history = useHistory();

  const [userProfile, setUserProfile] = useState<JamProfile>({
    email: '',
    nickname: '',
  });

  const [displayNameError, setDisplayNameError] = useState('');
  const [loading, setLoading] = useState<boolean>(true);

  const displayNameInputRef = useRef<InputProps.Ref>();

  useEffect(() => {
    if (!user) {
      return;
    }

    setUserProfile({ email: user.email, nickname: user.nickname ?? '' });
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (loading) return;

    history.block(() => {
      if (!userProfile.nickname) {
        alert(t(i18nKeys.JamProfile.jamProfileDetails.incompleteProfile));
        return false;
      }
    });
  }, [t, loading, history, userProfile]);

  const validateDisplayName = () => {
    const isValid = !!userProfile.nickname && userProfile.nickname?.length > 3;

    if (!isValid) {
      setDisplayNameError(t(i18nKeys.createJamProfile.form.validation.displayNameUniqueAndShortError));
    }

    return isValid;
  };

  const onSubmit = async () => {
    if (!validateDisplayName()) {
      displayNameInputRef.current?.focus();
      return;
    }

    const body = {
      nickname: userProfile.nickname,
      email: userProfile.email,
    };

    setLoading(true);
    try {
      await jamProfileAPI.updateProfile(body);
      onNicknameChange(userProfile.nickname);
    } catch (error) {
      localLogger('error', error);
    }
    setLoading(false);
  };

  if (loading) {
    return <JamSpinner />;
  }

  return (
    <ContentLayout
      header={
        <SpaceBetween size="m">
          <Header variant="h1" data-testid="profile-details-header">
            {t(i18nKeys.JamProfile.jamProfileDetails.title)}
          </Header>
        </SpaceBetween>
      }
    >
      <Grid gridDefinition={[{ colspan: 10 }]}>
        <div>
          <Container>
            <div className="form-space">
              <form onSubmit={(e) => e.preventDefault()}>
                <SpaceBetween direction="vertical" size="l">
                  <FormField
                    stretch
                    label={`${t(i18nKeys.createJamProfile.form.displayName)}*`}
                    description={t(i18nKeys.createJamProfile.form.displayNameDesc)}
                    errorText={displayNameError}
                    i18nStrings={{ errorIconAriaLabel: t(i18nKeys.general.error) }}
                  >
                    <Input
                      value={userProfile.nickname}
                      onChange={({ detail }) => {
                        setDisplayNameError('');
                        setUserProfile({ ...userProfile, nickname: detail.value });
                      }}
                      ref={displayNameInputRef as any}
                      onBlur={() => validateDisplayName()}
                      data-testid="displayname-input"
                    />
                  </FormField>
                </SpaceBetween>
              </form>
            </div>
          </Container>
          <div className="update-btn">
            <Box float="right">
              <Button
                variant="primary"
                ariaLabel={t(i18nKeys.JamProfile.jamProfileDetails.btn.updateProfile)}
                onClick={() => void onSubmit()}
                data-testid="profile-submit-button"
              >
                {t(i18nKeys.JamProfile.jamProfileDetails.btn.updateProfile)}
              </Button>
            </Box>
          </div>
        </div>
      </Grid>
    </ContentLayout>
  );
};

export default jamProfileDetails;
